import { Link } from 'react-router';

import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { StorefrontPageSections } from '@/features/storefront/sections/StorefrontPageSections';
import { usePageSections } from '@/features/storefront/sections/usePageSections';
import { safeHref } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { useStorefrontMeta } from '@/features/storefront/useStorefrontMeta';

/**
 * The shop's own dead end.
 *
 * A shopper who mistypes an address or follows a link from an email sent six
 * months ago used to land on the platform's 404 - no header, no footer, no way
 * back into the shop they were trying to buy from. This page is inside the
 * storefront's layout, wears the merchant's theme, and is arrangeable like
 * every other page: what a shop puts here is usually its best sellers and a
 * search box, because a lost shopper is still a shopper.
 *
 * Every setting is blank by default and every blank keeps the shop's own
 * words, so a merchant who never opens this page still gets a 404 that looks
 * like their shop.
 */
export function StorefrontNotFoundPage() {
  const paths = useStorefrontPaths();
  const text = useShopContent();
  const { sections, anchor } = usePageSections('notFound');
  const settings = anchor?.type === 'not_found' ? anchor : null;

  useStorefrontMeta({ title: settings?.title || text('not_found.title') });

  const label = settings?.cta_label || text('not_found.cta');
  const href = safeHref(settings?.cta_href) || paths.home();

  const body = (
    <Container className="py-20 text-center">
      <h1 className="text-3xl sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
        {settings?.title || text('not_found.title')}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm" style={{ color: 'var(--storefront-muted)' }}>
        {settings?.body || text('not_found.body')}
      </p>
      {/* Off-site links leave the router alone; a shop path stays a route, so
          the shopper is back in the storefront rather than reloading it. */}
      {/^https?:\/\//i.test(href) ? (
        <a
          href={href}
          rel="noreferrer"
          className="mt-8 inline-flex h-12 items-center px-7 text-sm font-semibold"
          style={STOREFRONT_BUTTON_STYLE}
        >
          {label}
        </a>
      ) : (
        <Link
          to={paths.menu(href)}
          className="mt-8 inline-flex h-12 items-center px-7 text-sm font-semibold"
          style={STOREFRONT_BUTTON_STYLE}
        >
          {label}
        </Link>
      )}
    </Container>
  );

  return <StorefrontPageSections sections={sections} slots={{ not_found: body }} />;
}
